import { Input } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Search, Users } from "lucide-react";
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import { AdminUserRow, useAdminUsers } from "@/admin/services";

import { AdminDataTable } from "../../components/ui/data-table";
import { EmptyState } from "../_components/empty-state";
import { PageHeader } from "../_components/page-header";

const PLAN_BADGE_CLASSES: Record<string, string> = {
  pro: "bg-success-soft text-success-foreground",
  starter_pass: "bg-primary-soft text-primary",
  lifetime: "bg-violet-50 text-violet-700",
  free: "bg-surface text-muted-foreground",
};

export const AdminUsersPage = () => {
  const [query, setQuery] = React.useState("");
  const debouncedQuery = useDebounced(query, 250);
  const { data, isLoading, isFetching } = useAdminUsers(debouncedQuery);
  const navigate = useNavigate();

  const columns = React.useMemo<ColumnDef<AdminUserRow>[]>(
    () => [
      {
        accessorKey: "email",
        header: "User",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {row.original.name || row.original.username}
            </span>
            <span className="text-[12px] text-muted-foreground">{row.original.email}</span>
          </div>
        ),
      },
      {
        accessorKey: "plan",
        header: "Plan",
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]",
              PLAN_BADGE_CLASSES[row.original.plan] ?? PLAN_BADGE_CLASSES.free,
            )}
          >
            {row.original.plan}
          </span>
        ),
      },
      {
        accessorKey: "subscriptionStatus",
        header: "Status",
        cell: ({ row }) => (
          <span className="text-[12px] text-muted-foreground">
            {row.original.subscriptionStatus ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "provider",
        header: "Provider",
        cell: ({ row }) => (
          <span className="text-[12px] capitalize text-muted-foreground">
            {row.original.provider}
          </span>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) =>
          row.original.role === "admin" ? (
            <span className="inline-flex items-center rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">
              Admin
            </span>
          ) : (
            <span className="text-[12px] text-subtle">—</span>
          ),
      },
      {
        accessorKey: "createdAt",
        header: "Joined",
        cell: ({ row }) => (
          <span className="text-[12px] tabular-nums text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "id",
        header: "ID",
        cell: ({ row }) => (
          // ID is the second-most-useful column on this page (after the
          // human-readable user identifier) — it's the join key for Stripe,
          // PostHog, and Sentry. Mono so it's visually unmistakable as a
          // machine string and so monospaced eye-scanning works.
          <span className="mono text-[11px] text-subtle">{row.original.id.slice(0, 10)}…</span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Users · Admin</title>
      </Helmet>

      <PageHeader
        title="Users"
        description="Search by email, name, username, or Stripe customer ID."
        trail={["Admin"]}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="relative w-full sm:max-w-md sm:flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-subtle" />
          {/* This is the page's primary action — autofocus is intentional
              and matches Stripe Dashboard's user-search behaviour. */}
          <Input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users…"
            className="h-8 border-border bg-card pl-8 text-[13px]"
          />
        </div>
        <span className="text-[12px] text-muted-foreground tabular-nums">
          {isFetching
            ? "Searching…"
            : (data?.length ?? 0) === 0
              ? ""
              : `${data?.length} result${data!.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {!isLoading && (data?.length ?? 0) === 0 && debouncedQuery.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Search for a user"
          description="Type at least two characters to start searching."
        />
      ) : (
        <AdminDataTable
          columns={columns}
          data={data ?? []}
          loading={isLoading}
          onRowClick={(row) => navigate(`/users/${row.id}`)}
        />
      )}
    </>
  );
};

const useDebounced = <T,>(value: T, ms: number): T => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(handle);
  }, [value, ms]);
  return debounced;
};

export default AdminUsersPage;
